import {
    Ref,
    useCallback,
    useRef,
    useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Stack, TextField } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { EditDrawer } from 'common/components/EditDrawer';
import { DEFAULT_SUCCESS_MESSAGE } from 'common/components/EditDrawer/EditDrawer';
import { closeDrawer, selectCurrentOperation, selectNameInConflict, setNameInConflict, setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { postApplicationSuite, putApplicationSuite } from 'common/services/api/ApplicationSuites/ApplicationSuites.thunks';
import { EditDrawerContentProps, IEditingItemInfo,Operations,  } from 'common/types/TypeDefs';
import { handleEditDrawerError } from 'common/utils/errorHandlers';
import { editDrawerTextFieldStyles } from 'common/utils/StyleHelpers';
import { useSnackbar } from 'notistack';

const constructCreateContentComponent = (
    title: string,
    saveButtonFunction: () => void,
    applicationSuiteNameFieldRef: Ref<HTMLInputElement>,
    handleSaveBtnEnabled: () => void,
    nameInConflict: boolean,
    processing: boolean
) => {
    return (
        <EditDrawer
            title={title}
            saveButtonFunction={saveButtonFunction}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    inputRef={applicationSuiteNameFieldRef}
                    id="filled-basic"
                    size="small"
                    label="Application Suite Name"
                    placeholder="Type Application Suite Name"
                    variant="filled"
                    focused
                    autoFocus={true}
                    onChange={handleSaveBtnEnabled}
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                />
                {nameInConflict &&
                  <Box component='div'
                      display='inline'
                      sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    Application Suite name already in use.
                  </Box>
                }
            </Stack>
        </EditDrawer>
    );
};

const constructEditContentComponent = (
    title: string,
    saveButtonFunction: () => void,
    applicationSuiteNameFieldRef: Ref<HTMLInputElement>,
    handleSaveBtnEnabled: () => void,
    nameInConflict: boolean,
    processing: boolean,
    editingItemInfo: IEditingItemInfo<{ name: string }>,
) => {
    const { editingItemId, editingRow } = editingItemInfo;
    return (
        <EditDrawer
            title={title}
            saveButtonFunction={saveButtonFunction}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    inputRef={applicationSuiteNameFieldRef}
                    id="filled-basic"
                    size="small"
                    label="Application Suite Name"
                    placeholder="Edit Application Suite Name"
                    defaultValue={editingRow?.name}
                    variant="filled"
                    focused
                    autoFocus={true}
                    autoComplete={'off'}
                    onChange={handleSaveBtnEnabled}
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                />
                {nameInConflict &&
                    <Box component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                        Application Suite name already in use.
                    </Box>
                }
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    id="filled-basic"
                    size="small"
                    label="Application Suite ID"
                    defaultValue={editingItemId}
                    variant="filled"
                    disabled
                />
            </Stack>
        </EditDrawer>
    );
};

export const ApplicationSuitesCreateContent = ({
    title,
    editingItemInfo,
}: EditDrawerContentProps<{ name: string }>) => {
    const applicationSuiteNameFieldRef = useRef<HTMLInputElement>(undefined);
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [processing, setProcessing] = useState<boolean>(false);
    const currentOperation = useAppSelector(selectCurrentOperation);

    const nameInConflict = useAppSelector(selectNameInConflict);

    const dispatch = useAppDispatch();
    const handleSaveBtnEnabled = () => dispatch(setSaveButtonEnabled(!!applicationSuiteNameFieldRef?.current?.value));

    const editSuccessHandler = (): void => {
        dispatch(setNameInConflict(false));
        dispatch(closeDrawer());
        enqueueSnackbar(DEFAULT_SUCCESS_MESSAGE, { variant: 'success' });
    };

    const createSuccessHandler = (id: string, message?: string | null): void => {
        dispatch(closeDrawer());
        navigate(`${id}/tenants`);
        if(message) {
            enqueueSnackbar(
                'Tenant could not be automatically created. Please add Tenant manually.',
                { variant: 'error' }
            );
        }
    };

    const applicationSuitePutCallback = useCallback(() => {
        if (!editingItemInfo?.editingItemId) {
            return;
        }
        const suiteOptions = {
            id: editingItemInfo.editingItemId.toString(),
            name: applicationSuiteNameFieldRef.current?.value ?? '',
        };
        setProcessing(true);
        dispatch(putApplicationSuite(suiteOptions))
            .unwrap()
            .then((result) => {
                dispatch(setRows(result.rows));
                editSuccessHandler();
                setProcessing(false);
            })
            .catch((error) => {
                handleEditDrawerError(Operations.Edit, extractStatusCode(error.message), 'Application Suite', dispatch, enqueueSnackbar);
                setProcessing(false);
            });
    }, [editingItemInfo?.editingItemId]);

    const applicationSuitePostCallback = useCallback(() => {
        const suiteOptions = {
            name: applicationSuiteNameFieldRef.current?.value ?? ''
        };
        setProcessing(true);
        dispatch(postApplicationSuite(suiteOptions))
            .unwrap()
            .then((result) => {
                createSuccessHandler(result.response.data.suiteId, result.response.error);
                setProcessing(false);
            })
            .catch((error) => {
                handleEditDrawerError(Operations.Create, extractStatusCode(error.message), 'Application Suite', dispatch, enqueueSnackbar);
                setProcessing(false);
            });
    }, []);

    if (currentOperation === Operations.Create) {
        return constructCreateContentComponent(
            title,
            applicationSuitePostCallback,
            applicationSuiteNameFieldRef as Ref<HTMLInputElement>,
            handleSaveBtnEnabled,
            nameInConflict ?? false,
            processing
        );
    } else if (currentOperation === Operations.Edit) {
        if (!editingItemInfo) {
            return null;
        }
        return constructEditContentComponent(
            title,
            applicationSuitePutCallback,
            applicationSuiteNameFieldRef as Ref<HTMLInputElement>,
            handleSaveBtnEnabled,
            nameInConflict ?? false,
            processing,
            editingItemInfo
        );
    }
    return null;
};
